import { useQueries } from '@tanstack/react-query';
import { checkAgentEnabled, type AgentVisibilityScope } from '../api/agent-visibility.api';

/**
 * Hook to check agent visibility for specific agents in the given workspace
 * and organization scope.
 *
 * `entities/` code MUST NOT value-import from
 * sibling entities. The caller is responsible for resolving `workspaceId`
 * and `organizationId` (e.g. via `useCurrentWorkspaceId` from
 * `@/entities/workspace` and `getCurrentOrganizationId` from `@/entities/user`)
 * and passing them in.
 *
 * The TanStack Query cache key is `['agent-visibility', workspaceId, agentId]`
 * so callers that share a `QueryClient` continue to share the cache for the
 * same agent + workspace.
 *
 * @param agentIds - Array of agent IDs to check visibility for
 * @param scope - Workspace + organization context for the access check
 * @returns Object with helper to check if specific agent is enabled and loading state
 */
export const useAgentVisibility = (agentIds: string[] = [], scope: AgentVisibilityScope) => {
  const { workspaceId, organizationId } = scope;

  // `useQueries` with `combine` builds a stable result object reference-equal
  // across renders when the underlying query results haven't changed. This
  // prevents downstream consumers (e.g. `useSmartStartCards`, page-level
  // memoised callbacks) from re-rendering on every parent render.
  return useQueries({
    queries: agentIds.map((agentId) => ({
      // `organizationId` is intentionally NOT part of the queryKey: it is
      // invariant per signed-in session (one user → one org), so adding it
      // would (a) break the cache-key contract
      // (`['agent-visibility', workspaceId, agentId]`) without any observable
      // cache-isolation benefit, and (b) regress the cache-compat test that
      // locks the 3-element key shape.
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: ['agent-visibility', workspaceId, agentId],
      queryFn: () => checkAgentEnabled(agentId, { workspaceId, organizationId }),
      enabled: !!workspaceId && !!agentId,
      staleTime: 60 * 60 * 1000, // 1 hour
      gcTime: 60 * 60 * 1000, // 1 hour
    })),
    combine: (results) => {
      // Build a stable map from agentId → enabled flag and a stable
      // `isAgentEnabled` reference. `combine` only re-runs when the
      // underlying query results actually change.
      const enabledByAgentId = new Map<string, boolean>();
      let isLoading = false;
      for (let i = 0; i < agentIds.length; i++) {
        const result = results[i];
        enabledByAgentId.set(agentIds[i], result.data ?? false);
        if (result.isLoading) isLoading = true;
      }
      return {
        isLoading,
        isAgentEnabled: (agentId: string): boolean => enabledByAgentId.get(agentId) ?? false,
      };
    },
  });
};
